import {
	ConfigurationLabel,
	ConfigurationLabelValue,
	isConfigurationLabel,
	isPlatformConfiguration,
	LabelsExcludedFromDashboard,
	PlatformConfiguration
} from '../../../common/types/ConfigurationTypes';

const extractLabels = (labels: unknown): ConfigurationLabel[] => {
	if (!Array.isArray(labels)) {
		return [];
	}

	// Validate each label to ensure type safety
	return labels.filter(isConfigurationLabel);
};

export const extractAdditionalLabels = (platform: PlatformConfiguration): ConfigurationLabel[] =>
	extractLabels(platform.ADDITIONAL_LABELS);

export const extractCoreLabels = (platform: PlatformConfiguration): ConfigurationLabel[] =>
	extractLabels(platform.CORE_LABELS);

export const extractMobileCoreLabels = (platform: PlatformConfiguration): ConfigurationLabel[] =>
	extractLabels(platform.MOBILE_CORE_LABELS);

export const isValidPlatformConfiguration = (
	platform: unknown
): platform is PlatformConfiguration => {
	return isPlatformConfiguration(platform);
};

export const processPlatformConfiguration = (
	platform: unknown
): {
	additionalLabels: ConfigurationLabel[];
	coreLabels: ConfigurationLabel[];
	mobileCoreLabels: ConfigurationLabel[];
	isValid: boolean;
} => {
	if (!isValidPlatformConfiguration(platform)) {
		return {
			additionalLabels: [],
			coreLabels: [],
			mobileCoreLabels: [],
			isValid: false
		};
	}

	const additionalLabels = extractAdditionalLabels(platform);
	const coreLabels = extractCoreLabels(platform);
	const mobileCoreLabels = extractMobileCoreLabels(platform);

	return {
		additionalLabels,
		coreLabels,
		mobileCoreLabels,
		isValid: true
	};
};

export const buildLabelsExcludedFromDashboard = (
	coreLabels?: ConfigurationLabel[]
): LabelsExcludedFromDashboard => {
	if (!coreLabels?.length) {
		return {};
	}

	const labelsExcludedFromDashboard: LabelsExcludedFromDashboard = {};

	coreLabels.forEach((label) => {
		const excludedValues = (label.values ?? []).filter(
			(v): v is ConfigurationLabelValue =>
				typeof v === 'object' &&
				!!v &&
				(v as ConfigurationLabelValue).excludedFromDashboard === true
		) as ConfigurationLabelValue[];

		if (excludedValues.length > 0) {
			labelsExcludedFromDashboard[label.label] = excludedValues.map((ev) => ({
				value: ev.value,
				label: ev.value
			}));
		}
	});

	return labelsExcludedFromDashboard;
};
